import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { toMajor } from '../../contracts/money';
import { CheckCircle2, Headphones, MapPin, Package, Plus, Users, X } from 'lucide-react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { PolicyConsentCheckbox } from '../../components/PolicyConsent';
import { Card } from '../../components/Card';
import { Drawer } from '../../components/Drawer';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { useActiveBusiness, useAddOns, queryKeys } from '../../api/hooks';
import { apiPost, apiDelete } from '../../api/client';
import { useQueryClient } from '@tanstack/react-query';

type ActiveAddon = {
  id: string;
  name: string;
  unitPriceMinor: number;
  qty: number;
  used?: number;
  limit?: number;
  unitLabel: string;
  icon: typeof MapPin;
  /** The API already sends this for ACTIVE add-ons; the mapping used to drop it, so a
   *  one-time purchase was displayed to the customer as a recurring "/ month" charge. */
  oneTime?: boolean;
};

type AvailableAddon = {
  id: string;
  name: string;
  unitPriceMinor: number;
  description: string;
  oneTime?: boolean;
};

const ICON_BY_NAME: Record<string, typeof MapPin> = {
  'Extra location': MapPin,
  'Extra user': Users,
  'Priority support': Headphones,
  'SMS pack (1,000)': Headphones,
};

/**
 * Add-ons page — §7.10
 * Lists in-use add-ons with adjust/remove actions and a section for browsing more.
 */
export function AddOns() {
  const { data: business } = useActiveBusiness();
  const { data: addOnData } = useAddOns(business?.id ?? '');
  const qc = useQueryClient();
  const [active, setActive] = useState<ActiveAddon[]>([]);
  const [available, setAvailable] = useState<AvailableAddon[]>([]);
  const [adding, setAdding] = useState<AvailableAddon | null>(null);
  const [removing, setRemoving] = useState<ActiveAddon | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ addonName: string; invoiceNo: string } | null>(null);
  const availableRef = useRef<HTMLDivElement>(null);

  // Initialize from the real subscription add-ons + catalog (mutations below stay
  // local/optimistic — the actual purchase/cancel flow is a follow-on).
  useEffect(() => {
    if (!addOnData) return;
    setActive(addOnData.active.map((a) => ({ id: a.id, name: a.name, unitPriceMinor: a.unitPriceMinor, qty: a.qty, unitLabel: 'unit', icon: ICON_BY_NAME[a.name] ?? Package, oneTime: a.oneTime })));
    setAvailable(addOnData.available.map((a) => ({ id: a.id, name: a.name, unitPriceMinor: a.unitPriceMinor, description: a.description, oneTime: a.oneTime })));
  }, [addOnData]);

  // Persist FIRST, then reflect the result — the drawer awaits this and only shows its success
  // screen (with the real invoiceNo) once the API actually resolves; a rejection (409 already
  // added / 400 tier-incompatible / 404 gone) propagates so the drawer can show an inline error
  // without ever listing the add-on as active. Attaching bills immediately (service-first: an
  // OPEN invoice is minted for the add-on's price).
  async function handleAdd(addon: AvailableAddon, qty: number): Promise<{ invoiceNo?: string }> {
    if (!business?.id) throw new Error('No active business.');
    const res = await apiPost<{ invoiceNo?: string }>(`/businesses/${business.id}/add-ons/${addon.id}`);
    setActive((prev) => [...prev, { id: addon.id, name: addon.name, unitPriceMinor: addon.unitPriceMinor, qty, unitLabel: 'unit', icon: ICON_BY_NAME[addon.name] ?? Package, oneTime: addon.oneTime }]);
    setAvailable((prev) => prev.filter((a) => a.id !== addon.id));
    if (res.invoiceNo) setNotice({ addonName: addon.name, invoiceNo: res.invoiceNo });
    void qc.invalidateQueries({ queryKey: ['addons', business.id] });
    void qc.invalidateQueries({ queryKey: queryKeys.invoices(business.id) });
    return res;
  }

  async function handleRemove(addon: ActiveAddon) {
    // Optimistic removal is fine, but a FAILED delete must not look identical to a
    // successful one. Previously the row vanished, the catch-less `finally` swallowed
    // the error, and the customer believed the add-on (and its charge) was gone.
    setActive((prev) => prev.filter((a) => a.id !== addon.id));
    setRemoving(null);
    if (business?.id) {
      try {
        await apiDelete(`/businesses/${business.id}/add-ons/${addon.id}`);
      } catch {
        setRemoveError(`Could not remove ${addon.name}. It is still active — please try again.`);
      } finally {
        void qc.invalidateQueries({ queryKey: ['addons', business.id] });
      }
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-4xl mx-auto">
      <PageHeader
        eyebrow="Subscription"
        title="Add-ons"
        subtitle="Boost the limits of your plan without changing tiers."
        action={
          <Button
            variant="secondary"
            iconLeft={<Plus className="w-4 h-4" />}
            onClick={() => availableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            Browse more
          </Button>
        }
      />

      {notice && (
        <Banner tone="success" title="Added" onDismiss={() => setNotice(null)} className="mb-6">
          {notice.addonName} is on your subscription — invoice {notice.invoiceNo} is on your{' '}
          <Link to="/billing" className="underline font-medium">Billing</Link> page.
        </Banner>
      )}

      {removeError && (
        <Banner tone="error" title="Could not remove add-on" onDismiss={() => setRemoveError(null)} className="mb-6">
          {removeError}
        </Banner>
      )}

      <Card className="mb-6 overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">In use</h2>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {active.length} active
          </span>
        </div>

        {active.length === 0 && (
          <div className="px-5 sm:px-6 py-12 text-center">
            <p className="text-sm text-zinc-500 dark:text-zinc-400">No add-ons yet — browse below.</p>
          </div>
        )}

        <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {active.map((a) => {
            const Icon = a.icon;
            const monthly = a.unitPriceMinor * a.qty;
            return (
              <li key={a.id} className="px-5 sm:px-6 py-4 flex items-start gap-4 flex-wrap">
                <span className="w-10 h-10 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-zinc-950 dark:text-zinc-50">{a.name}</span>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">×{a.qty}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
                    <span className="tabular-nums">
                      <Money minor={monthly} hideCents tone="neutral" />{a.oneTime ? ' one-time' : ' / month'}
                    </span>
                    {a.used !== undefined && a.limit !== undefined && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span className="tabular-nums">
                          {a.used} / {a.limit} used
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setRemoving(a)}
                    className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/30 dark:hover:text-rose-400 transition"
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <div ref={availableRef}>
        <Card className="overflow-hidden">
          <div className="px-5 sm:px-6 py-4 border-b border-zinc-200 dark:border-zinc-700">
            <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Available</h2>
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              Compatible with your current plan.
            </p>
          </div>

          {available.length === 0 ? (
            <div className="px-5 sm:px-6 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
              You've added every available add-on.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {available.map((a) => (
                <li key={a.id} className="px-5 sm:px-6 py-4 flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-950 dark:text-zinc-50">{a.name}</div>
                    <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300">
                      {a.description}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Money
                      minor={a.unitPriceMinor}
                      hideCents
                      tone="neutral"
                      className="font-medium text-zinc-950 dark:text-zinc-50"
                    />
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {a.oneTime ? 'one-time' : '/ month'}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    iconLeft={<Plus className="w-3.5 h-3.5" />}
                    onClick={() => setAdding(a)}
                  >
                    Add
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {adding && (
        <AddDrawer
          addon={adding}
          onClose={() => setAdding(null)}
          onConfirm={(qty) => handleAdd(adding, qty)}
        />
      )}
      {removing && (
        <RemoveDrawer
          addon={removing}
          onClose={() => setRemoving(null)}
          onConfirm={() => handleRemove(removing)}
        />
      )}
    </div>
  );
}

function AddDrawer({
  addon,
  onClose,
  onConfirm,
}: {
  addon: AvailableAddon;
  onClose: () => void;
  onConfirm: (qty: number) => Promise<{ invoiceNo?: string }>;
}) {
  const [qty, setQty] = useState(1);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ invoiceNo?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const total = addon.unitPriceMinor * qty;

  async function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await onConfirm(qty);
      setDone(res);
      setTimeout(onClose, 900);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(
        status === 409
          ? 'This add-on is already on your subscription.'
          : status === 400
            ? 'This add-on is not available on your current plan.'
            : status === 404
              ? 'This add-on is no longer available.'
              : 'Could not add this add-on. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={done ? 'Added' : `Add — ${addon.name}`}
      width="md"
      footer={
        done ? null : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={confirm} loading={submitting} disabled={!accepted}>Add add-on</Button>
          </>
        )
      }
    >
      {done ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Add-on activated</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {addon.name} is live on your subscription
            {done.invoiceNo ? ` — invoice ${done.invoiceNo} is on your Billing page.` : '.'}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-zinc-700 dark:text-zinc-200">{addon.description}</p>

          {!addon.oneTime && (
            <div>
              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Quantity</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 rounded-md border border-zinc-300 dark:border-zinc-700 grid place-items-center text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || '1', 10)))}
                  className={cn(
                    'w-24 px-3 py-2.5 text-center text-sm tabular-nums rounded-md',
                    'bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50',
                    'border border-zinc-300 dark:border-zinc-700',
                    'focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm',
                  )}
                />
                <button
                  type="button"
                  onClick={() => setQty((q) => q + 1)}
                  className="w-10 h-10 rounded-md border border-zinc-300 dark:border-zinc-700 grid place-items-center text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  +
                </button>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400">
              {addon.oneTime ? 'One-time charge' : 'Recurring charge'}
            </div>
            <dl className="text-sm space-y-2">
              <div className="flex items-center justify-between">
                <dt className="text-zinc-600 dark:text-zinc-300">
                  {qty} × ৳{toMajor(addon.unitPriceMinor)}{addon.oneTime ? '' : ' / month'}
                </dt>
                <dd className="tabular-nums font-medium text-zinc-950 dark:text-zinc-50">
                  <Money minor={total} hideCents tone="neutral" />{addon.oneTime ? '' : ' / mo'}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Added to your subscription now and billed on your next invoice.
            </p>
          </div>

          {/* Buying an add-on commits a charge, so the same consent applies here. */}
          <PolicyConsentCheckbox checked={accepted} onChange={setAccepted} id="addon-consent" />

          {error && (
            <Banner tone="error" title="Couldn’t add this add-on">
              {error}
            </Banner>
          )}
        </div>
      )}
    </Drawer>
  );
}

function RemoveDrawer({
  addon,
  onClose,
  onConfirm,
}: {
  addon: ActiveAddon;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);

  function confirm() {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onConfirm();
    }, 500);
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={`Remove — ${addon.name}`}
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Keep it</Button>
          <Button variant="destructive" onClick={confirm} loading={submitting}>
            Remove add-on
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* The old copy promised "any unused portion is credited to your account toward
            your next invoice". No credit is issued anywhere on this path — the API
            simply detaches the add-on. Promising a refund the system never makes is
            worse than charging for the remainder, so the copy now matches reality. */}
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          You'll lose {addon.qty} {addon.unitLabel}{addon.qty === 1 ? '' : 's'} of capacity straight away.
          The current period is already paid for and isn't refunded.
        </p>
        {addon.used !== undefined && addon.limit !== undefined && addon.used > addon.limit - addon.qty && (
          <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4 text-sm text-rose-800 dark:text-rose-300">
            You're using {addon.used} {addon.unitLabel}s — removing this drops your cap to{' '}
            {addon.limit - addon.qty}, which is below current usage. New {addon.unitLabel}s will be blocked
            until usage drops or you re-add.
          </div>
        )}
      </div>
    </Drawer>
  );
}

